/**
 * PartnerStep Component
 *
 * Third step of the onboarding wizard.
 * Collects the primary conversation partner's name, pronouns, and relationship type.
 */

import { useState } from "react";
import { ArrowRight } from "lucide-react";
import { Button } from "../../ui/button";
import { useProfileOptions } from "../../../hooks/useCurrentUser";
import { useCreateConversationPartner } from "../../../hooks/useConversationPartners";
import {
  PARTNER_TYPE_LABELS,
  type ConversationPartnerType,
} from "../../../types/conversationPartner";

interface PartnerStepProps {
  onContinue: () => void;
  isLoading: boolean;
}

const partnerTypes: ConversationPartnerType[] = [
  "primary_partner",
  "therapist",
  "friend",
  "family",
  "other",
];

export function PartnerStep({ onContinue, isLoading }: PartnerStepProps) {
  const { data: profileOptions } = useProfileOptions();
  const createPartner = useCreateConversationPartner();

  const [name, setName] = useState("");
  const [pronouns, setPronouns] = useState("");
  const [partnerType, setPartnerType] =
    useState<ConversationPartnerType>("primary_partner");

  const canContinue = name.trim();

  const handleContinue = async () => {
    if (!canContinue) return;

    try {
      await createPartner.mutateAsync({
        name: name.trim(),
        pronouns: pronouns || undefined,
        partner_type: partnerType,
        is_primary: true,
      });
      onContinue();
    } catch {
      // Error handled by mutation state
    }
  };

  return (
    <div className="space-y-6 py-4">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold text-foreground">Your Partner</h2>
        <p className="text-muted-foreground">
          Tell us about the person you'll be analyzing conversations with
        </p>
      </div>

      <div className="max-w-sm mx-auto space-y-4">
        <div className="space-y-2">
          <label
            htmlFor="partnerName"
            className="text-sm font-medium text-foreground"
          >
            Their Name
          </label>
          <input
            id="partnerName"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Partner's first name"
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>

        <div className="space-y-2">
          <label
            htmlFor="partnerPronouns"
            className="text-sm font-medium text-foreground"
          >
            Their Pronouns{" "}
            <span className="text-muted-foreground font-normal">
              (optional)
            </span>
          </label>
          <select
            id="partnerPronouns"
            value={pronouns}
            onChange={(e) => setPronouns(e.target.value)}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <option value="">Select pronouns</option>
            {profileOptions?.pronouns.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-2">
          <label
            htmlFor="partnerType"
            className="text-sm font-medium text-foreground"
          >
            Relationship Type
          </label>
          <select
            id="partnerType"
            value={partnerType}
            onChange={(e) =>
              setPartnerType(e.target.value as ConversationPartnerType)
            }
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {partnerTypes.map((type) => (
              <option key={type} value={type}>
                {PARTNER_TYPE_LABELS[type]}
              </option>
            ))}
          </select>
        </div>

        {createPartner.isError && (
          <p className="text-sm text-destructive">
            Something went wrong. Please try again.
          </p>
        )}
      </div>

      <div className="text-center">
        <Button
          size="lg"
          onClick={handleContinue}
          disabled={!canContinue || createPartner.isPending || isLoading}
          className="gap-2"
        >
          {createPartner.isPending || isLoading ? "Saving..." : "Continue"}
          <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
